import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { PulsingText } from './pulsing-text';

interface JobProgressIndicatorProps {
  createdAt?: Date | string; // UTC time
  estimatedDuration?: number;
  progressHead?: string;
  onComplete?: () => void;
}

export const JobProgressIndicator = ({
  createdAt,
  estimatedDuration = 8,
  progressHead = 'Job in progress',
  onComplete,
}: JobProgressIndicatorProps) => {
  const [progress, setProgress] = useState(0);
  const [textIndex, setTextIndex] = useState(0);

  const progressTexts = [
    'Hang tight, this will only take a moment...',
    'Extracting and analyzing data...',
    'Generating visualizations...',
    'Preparing final results...',
  ];

  useEffect(() => {
    // Convert createdAt to local Date object if provided
    // JavaScript automatically converts UTC to local time when creating a Date object
    const startDate = createdAt ? new Date(createdAt) : new Date();
    const startTime = startDate.getTime();
    const now = Date.now();

    // Calculate total duration in milliseconds
    const totalDurationMs = estimatedDuration * 60 * 1000;

    // Ensure elapsed time is never negative (handles future dates)
    const initialElapsedMs = Math.max(0, now - startTime);

    // Cap progress at 99.9%
    const initialProgress = Math.min((initialElapsedMs / totalDurationMs) * 100, 99.9);
    setProgress(initialProgress);

    // Set the appropriate text based on progress
    const initialTextIndex = Math.min(
      Math.floor((initialElapsedMs / totalDurationMs) * progressTexts.length),
      progressTexts.length - 1,
    );
    setTextIndex(initialTextIndex);

    // Check if we should call onComplete right away
    if (initialProgress >= 99.9 && onComplete) {
      onComplete();
    }

    // Set up progress updates if not yet complete
    if (initialProgress < 99.9) {
      const updateInterval = 100;
      const timer = setInterval(() => {
        const currentElapsedMs = Math.max(0, Date.now() - startTime);
        const newProgress = Math.min((currentElapsedMs / totalDurationMs) * 100, 99.9);
        setProgress(newProgress);

        const newTextIndex = Math.min(
          Math.floor((currentElapsedMs / totalDurationMs) * progressTexts.length),
          progressTexts.length - 1,
        );
        if (newTextIndex !== textIndex) {
          setTextIndex(newTextIndex);
        }

        if (newProgress >= 99.9) {
          clearInterval(timer);
          if (onComplete) {
            onComplete();
          }
        }
      }, updateInterval);

      return () => clearInterval(timer);
    }
  }, [createdAt, estimatedDuration, textIndex, progressTexts.length, onComplete]);

  const radius = 35;

  return (
    <div className="flex items-center p-6 bg-gray-900 text-white rounded-lg w-full">
      <div className="relative mr-6">
        <svg className="w-24 h-24" viewBox="0 0 100 100">
          {/* Background circle */}

          {/* Gradient definitions */}
          <defs>
            <linearGradient id="spinnerGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#1E40AF" /> {/* Dark blue */}
              <stop offset="100%" stopColor="#ffffff" /> {/* White */}
            </linearGradient>
          </defs>

          {/* Animated full circle with gradient */}
          <motion.circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="url(#spinnerGradient)"
            strokeWidth="10"
            initial={{ rotate: 0 }}
            animate={{ rotate: 360 }}
            transition={{
              duration: 1.2,
              ease: 'linear',
              repeat: Infinity,
              repeatType: 'loop',
            }}
            style={{ transformOrigin: 'center' }}
          />

          {/* Percentage text */}
          <text x="50" y="55" fontSize="14" textAnchor="middle" fill="white" fontWeight="bold">
            {Math.round(progress)}%
          </text>
        </svg>
      </div>
      <div className="flex flex-col">
        <h3 className="text-lg font-semibold">{progressHead}</h3>
        <PulsingText text={progressTexts[textIndex]} />
      </div>
    </div>
  );
};
